import { z } from 'zod';
import { ErrorCode } from '../../errors/codes.js';
import { phoneSchema } from '../phone.js';

export const CALL_CENTER_LEAD_NAME_MAX = 200;
export const CALL_CENTER_LEAD_COMMENT_MAX = 2000;
export const CALL_CENTER_LEAD_AUDIO_URL_MAX = 2048;

/**
 * @aiPurpose HTTPS-only URL validator for a call-recording link
 *   submitted by an external call-center agent (#1256).
 * @aiSecurity rejects non-HTTPS schemes so a submitted recording link
 *   cannot smuggle `http`/`javascript`/`data` URLs to an internal
 *   consumer. The lead-detail view (#1260) renders this value.
 * @aiPii the URL may reference a recording of a customer call — redact
 *   in logs.
 */
const audioUrlSchema = z
  .string()
  .url()
  .max(CALL_CENTER_LEAD_AUDIO_URL_MAX)
  .refine((value) => value.startsWith('https://'), { message: 'audioUrl must be HTTPS' });

/**
 * @aiPurpose external call-center agent lead-intake submission body
 *   (#1256). The submit endpoint (#1258) accepts this shape from an
 *   authenticated `call_center_agent`; the source binding is resolved
 *   server-side from the agent's `CallCenterAgentProfile`, never from
 *   the body.
 * @aiInvariant `.strict()` blocks unknown keys. `phone` is strict E.164
 *   via the shared `phoneSchema`; `name` is required non-empty;
 *   `comment` and `audioUrl` are optional. `audioUrl` is HTTPS-only.
 * @aiPii `phone`, `name`, `comment`, `audioUrl` are PII — redact in
 *   logs. The external accepted response (`CallCenterLeadIntakeAccepted`)
 *   intentionally echoes NONE of these back.
 */
export const CallCenterLeadIntakeSubmissionSchema = z
  .object({
    phone: phoneSchema,
    name: z.string().trim().min(1).max(CALL_CENTER_LEAD_NAME_MAX),
    comment: z.string().max(CALL_CENTER_LEAD_COMMENT_MAX).optional(),
    audioUrl: audioUrlSchema.optional(),
  })
  .strict();
export type CallCenterLeadIntakeSubmission = z.infer<typeof CallCenterLeadIntakeSubmissionSchema>;

/**
 * @aiPurpose external accepted-response envelope for a call-center
 *   submission (#1256). Deliberately minimal: a bare `{ accepted: true }`
 *   acknowledgement.
 * @aiSecurity the external agent boundary must NOT leak server-side
 *   state — no lead id, public code, manager, dedupe result, or echoed
 *   PII. Internal consumers read the richer
 *   `LeadIntakeSubmissionHistoryItem` projection instead.
 */
export const CallCenterLeadIntakeAcceptedSchema = z
  .object({
    accepted: z.literal(true),
  })
  .strict();
export type CallCenterLeadIntakeAccepted = z.infer<typeof CallCenterLeadIntakeAcceptedSchema>;

/**
 * @aiPurpose closed catalog of call-center lead-intake submission
 *   outcomes (#1256). Mirrors the Prisma `LeadIntakeSubmissionOutcome`
 *   enum. Distinct from `LeadSourceDeliveryOutcome` (#1078 webhook
 *   intake).
 * @aiInvariant append-only; submission rows reference these literals
 *   forever. Adding a value requires a paired Prisma migration.
 */
export enum LeadIntakeSubmissionOutcome {
  CREATED_LEAD = 'CREATED_LEAD',
  DUPLICATE_EXISTING_LEAD = 'DUPLICATE_EXISTING_LEAD',
  REJECTED_VALIDATION = 'REJECTED_VALIDATION',
  REJECTED_SOURCE_DISABLED = 'REJECTED_SOURCE_DISABLED',
  REJECTED_NO_PROFILE = 'REJECTED_NO_PROFILE',
  INTERNAL_ERROR = 'INTERNAL_ERROR',
}
export const LeadIntakeSubmissionOutcomeSchema = z.nativeEnum(LeadIntakeSubmissionOutcome);

/**
 * @aiPurpose the `ErrorCode` members the call-center submit endpoint
 *   (#1258) returns through the standard `ApiError` envelope, surfaced as
 *   a named group so #1258/#1259 reference the same codes. Rejections flow
 *   through the shared `ApiError` contract — there is intentionally no
 *   parallel rejection schema.
 */
export const CALL_CENTER_INTAKE_ERROR_CODES = {
  noProfile: ErrorCode.CALL_CENTER_NO_PROFILE,
  sourceDisabled: ErrorCode.CALL_CENTER_SOURCE_DISABLED,
  // WHY (#1397): the agent re-submitted a phone already in their OWN accepted
  //   history. The deliberate own-history exception to the blind contract —
  //   scoped to `submittedByUserId = self`, never the global registry.
  ownDuplicate: ErrorCode.CALL_CENTER_DUPLICATE_OWN_PHONE,
  rateLimited: ErrorCode.RATE_LIMIT_EXCEEDED,
} as const;

/**
 * @aiPurpose internal-only projection of a `LeadIntakeSubmission` row for
 *   the lead-detail intake-history view (#1260).
 * @aiSecurity INTERNAL ONLY — gated by internal authorisation on the
 *   lead-detail route. Unlike the external accepted response, this shape
 *   MAY surface `phone`/`name`/`comment`/`audioUrl` because the consumer
 *   is an authenticated internal user already cleared to read the Lead's
 *   PII. Never expose this projection to the external agent boundary.
 * @aiPii surfaces `phone`, `name`, `comment`, `audioUrl` — redact in
 *   logs and never serialise to an external response.
 */
export const LeadIntakeSubmissionHistoryItemSchema = z
  .object({
    id: z.string().uuid(),
    leadId: z.string().uuid().nullable(),
    sourceId: z.string().uuid().nullable(),
    // WHY: the source's safe display name and the external submitter's
    //   display name (#1260 lead-detail history). NULL when the source/
    //   submitter row is missing or carries no safe name. NEVER carries the
    //   source HMAC secret — only the `LeadSource.displayName` is projected.
    sourceDisplayName: z.string().nullable(),
    submittedByUserId: z.string().uuid(),
    submittedByDisplayName: z.string().nullable(),
    outcome: LeadIntakeSubmissionOutcomeSchema,
    duplicateLeadId: z.string().uuid().nullable(),
    phone: z.string(),
    name: z.string(),
    comment: z.string().nullable(),
    audioUrl: z.string().nullable(),
    createdAt: z.string().datetime(),
  })
  .strict();
export type LeadIntakeSubmissionHistoryItem = z.infer<typeof LeadIntakeSubmissionHistoryItemSchema>;

/**
 * @aiPurpose ONE row of the external call-center agent's own-leads list
 *   (#1396 — `GET /api/call-center/my-leads`). A BLIND snapshot of the
 *   agent's own accepted lead-intake submission.
 * @aiSecurity EXTERNAL boundary — this is the agent's OWN snapshot and MUST
 *   NOT carry any server-side state: NO `leadId`, `publicCode`, `outcome`,
 *   `sourceId`, `duplicateLeadId`, manager, or stage. `id` is the
 *   `LeadIntakeSubmission.id` exposed as an OPAQUE UI key ONLY — it is not a
 *   Lead id and reveals nothing about downstream CRM state. `.strict()`
 *   blocks any field leaking through a future server-side change.
 * @aiPii `phone`, `name`, `comment`, `audioUrl` are PII — redact in logs;
 *   never log the raw values when serving this list.
 */
export const CallCenterMyLeadsItemSchema = z
  .object({
    id: z.string().uuid(),
    phone: z.string(),
    name: z.string(),
    comment: z.string().nullable(),
    audioUrl: z.string().nullable(),
    createdAt: z.string().datetime(),
  })
  .strict();
export type CallCenterMyLeadsItem = z.infer<typeof CallCenterMyLeadsItemSchema>;

/**
 * @aiPurpose response wire shape for `GET /api/call-center/my-leads`
 *   (#1396). Cursor-paginated, newest-first, one item per stored phone.
 * @aiSecurity `nextCursor` is the opaque keyset cursor; it carries only the
 *   `(createdAt, id)` sort key, never PII.
 */
export const CallCenterMyLeadsResponseSchema = z
  .object({
    items: z.array(CallCenterMyLeadsItemSchema),
    nextCursor: z.string().nullable(),
  })
  .strict();
export type CallCenterMyLeadsResponse = z.infer<typeof CallCenterMyLeadsResponseSchema>;

/**
 * @aiPurpose query string for `GET /api/call-center/my-leads` (#1396).
 *   Mirrors the `requests` list query shape — `z.coerce.number()` for the
 *   transport-string `limit`, an opaque `cursor`, and an optional `phone`
 *   substring filter.
 * @aiInvariant `.strict()` blocks unknown keys. `phone` is trimmed,
 *   1..32 chars (matches the stored `LeadIntakeSubmission.phone`
 *   `VarChar(32)` width); `limit` defaults to 50, capped at 100.
 * @aiPii a `phone` filter value is PII — redact in logs.
 */
export const CallCenterMyLeadsQuerySchema = z
  .object({
    phone: z.string().trim().min(1).max(32).optional(),
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
  })
  .strict();
export type CallCenterMyLeadsQuery = z.infer<typeof CallCenterMyLeadsQuerySchema>;

/**
 * @aiPurpose normalize a stored E.164 phone to the canonical key used by
 *   phone registries. The legacy helper name is retained for API
 *   compatibility, but runtime ownership is now the global
 *   `ContactPhoneKey.phoneKey` slot. The contract is
 *   digits-only: strip the leading `+` and every non-digit character so
 *   the key is a bare digit string (e.g. `+7 999` -> `7999`).
 * @aiInvariant deterministic and idempotent: `normalizeLeadPhoneKey(
 *   normalizeLeadPhoneKey(p)) === normalizeLeadPhoneKey(p)`.
 * @aiPii returned key is derived from a phone — redact in logs.
 */
export function normalizeLeadPhoneKey(phone: string): string {
  return phone.replace(/\D/g, '');
}

/**
 * @aiPurpose normalize an E.164 phone to the digits-only key stored in the
 *   single global `ContactPhoneKey.phoneKey` slot (#1359, cross-CRM phone
 *   uniqueness). Delegates to the legacy-named `normalizeLeadPhoneKey` so
 *   the regex is never duplicated.
 *   The leading `+` and every separator are stripped (e.g. `+7 999`
 *   -> `7999`, `+86-10-1234` -> `86101234`).
 * @aiInvariant deterministic and idempotent:
 *   `normalizeContactPhone(normalizeContactPhone(p)) ===
 *   normalizeContactPhone(p)`. Callers pass a strict-E.164 phone
 *   (`phoneSchema`); a bare `8…` Russian number normalizes to its literal
 *   digits, NOT to a `+7` form — country normalization is a caller
 *   concern, not this key's.
 * @aiPii the returned key is derived from a phone — redact in logs; the
 *   raw phone never appears in the key beyond its digits.
 */
export function normalizeContactPhone(e164: string): string {
  return normalizeLeadPhoneKey(e164);
}
